<script setup lang="ts">
import { ref, onMounted, onBeforeUnmount, computed, defineAsyncComponent } from 'vue'
import { useAuthStore } from '@/stores/authStore'
import { USER_ROLES } from '@/types'
import { useRouter } from 'vue-router'
import { ROUTES } from '@/router/routeNames'
import BusinessDashboard from './business/BusinessDashboard.vue'
import CustomerDashboard from './customer/CustomerDashboard.vue'
import DriverDashboard from './driver/DriverDashboard.vue'
import AdminDashboard from './admin/AdminDashboard.vue'
// Dynamic imports for dashboard components with error handling
// const BusinessDashboard = defineAsyncComponent({
//   loader: () => import('./business/BusinessDashboard.vue'),
//   loadingComponent: null,
//   errorComponent: null,
//   delay: 200,
//   timeout: 10000,
//   onError(error, retry, fail, attempts) {
//     console.error('Error loading BusinessDashboard:', error)
//     if (attempts <= 3) {
//       retry()
//     } else {
//       fail()
//     }
//   }
// })

// const CustomerDashboard = defineAsyncComponent({
//   loader: () => import('./customer/CustomerDashboard.vue'),
//   loadingComponent: null,
//   errorComponent: null,
//   delay: 200,
//   timeout: 10000,
//   onError(error, retry, fail, attempts) {
//     console.error('Error loading CustomerDashboard:', error)
//     if (attempts <= 3) {
//       retry()
//     } else {
//       fail()
//     }
//   }
// })

// const DriverDashboard = defineAsyncComponent({
//   loader: () => import('./driver/DriverDashboard.vue'),
//   loadingComponent: null,
//   errorComponent: null,
//   delay: 200,
//   timeout: 10000,
//   onError(error, retry, fail, attempts) {
//     console.error('Error loading DriverDashboard:', error)
//     if (attempts <= 3) {
//       retry()
//     } else {
//       fail()
//     }
//   }
// })

// const AdminDashboard = defineAsyncComponent({
//   loader: () => import('./admin/AdminDashboard.vue'),
//   loadingComponent: null,
//   errorComponent: null,
//   delay: 200,
//   timeout: 10000,
//   onError(error, retry, fail, attempts) {
//     console.error('Error loading AdminDashboard:', error)
//     if (attempts <= 3) {
//       retry()
//     } else {
//       fail()
//     }
//   }
// })

const authStore = useAuthStore()
const router = useRouter()
const isLoading = ref(true)
const loadError = ref(false)

// Computed property to determine which dashboard to show
const currentDashboard = computed(() => {
  const role = authStore.user?.role

  if (!role) return null

  switch (role) {
    case USER_ROLES.BUSINESS:
      return BusinessDashboard
    case USER_ROLES.DRIVER:
      return DriverDashboard
    case USER_ROLES.ADMIN:
      return AdminDashboard
    default:
      return CustomerDashboard
  }
})

// Function to refresh user data
const refreshUserData = async () => {
  isLoading.value = true
  loadError.value = false

  try {
    // Force refresh user data
    await authStore.getUser(true)

    // If no user is found after refresh, redirect to login
    if (!authStore.user) {
      router.push({ name: ROUTES.LOGIN })
      return
    }
  } catch (error) {
    console.error('Failed to refresh user data:', error)
    loadError.value = true
  } finally {
    isLoading.value = false
  }
}

// Refresh user data when component is mounted
onMounted(refreshUserData)

// Set up a listener for storage events (for multi-tab logout)
const handleStorageChange = (event) => {
  if (event.key === 'logout' && event.newValue === 'true') {
    refreshUserData()
  }
}

// Add storage event listener
onMounted(() => {
  window.addEventListener('storage', handleStorageChange)
})

// Clean up event listener
onBeforeUnmount(() => {
  window.removeEventListener('storage', handleStorageChange)
})
</script>

<template>
  <div>
    <!-- Loading state -->
    <div v-if="isLoading" class="flex justify-center items-center min-h-screen">
      <div class="w-12 h-12 rounded-full border-t-2 border-b-2 border-green-500 animate-spin"></div>
    </div>

    <!-- Error state -->
    <div v-else-if="loadError" class="flex flex-col justify-center items-center min-h-screen">
      <div class="mb-4 text-red-500">Failed to load dashboard</div>
      <button
        @click="refreshUserData"
        class="px-4 py-2 text-white bg-green-500 rounded transition-colors hover:bg-green-600"
      >
        Try Again
      </button>
    </div>

    <!-- No user state -->
    <div v-else-if="!authStore.user" class="flex justify-center items-center min-h-screen">
      <div class="text-center">
        <p class="mb-4">You need to be logged in to view this page</p>
        <button
          @click="router.push({ name: ROUTES.LOGIN })"
          class="px-4 py-2 text-white bg-green-500 rounded transition-colors hover:bg-green-600"
        >
          Go to Login
        </button>
      </div>
    </div>

    <!-- Dynamic dashboard component -->
    <component :is="currentDashboard" v-else />
  </div>
</template>
